import { DataStorage } from '@studyportals/data-storage';
import { IStudent, StudentField } from '@studyportals/studentdomain';
import merge from 'deepmerge';
import { Actor, AnonymousStudentProfileUpdated, InterestType, StudentRepositoryStateType } from '../../../interfaces';
import { WriteHistoryRecord } from '../../domain/dto/write-history-record';
import { AnonymousStudentEventBroadcaster } from '../anonymous-student-event-broadcaster';
import { StudentClient } from '../interfaces/student-client';

type StudentStorageData = Partial<Record<StudentField | InterestType, unknown>>;

export class LocalStudentClient implements StudentClient {
	private storageKeyWriteHistory: string;

	// tslint:disable: typedef
	constructor(
		private storageKey: string,
		private storageTTL: number,
		private anonymousStudentEventBroadcaster: AnonymousStudentEventBroadcaster | null = null
	) {
		this.storageKeyWriteHistory = `${storageKey}/WriteHistory`;
	}

	private localStorage = DataStorage;

	public cleanUp(): void {
		this.localStorage.remove(this.storageKey);
		this.localStorage.remove(this.storageKeyWriteHistory);
	}

	public async getData(studentFields: StudentField[]): Promise<IStudent> {
		const dataFromLocalStorage = this.retrieveDataFromLocalStorage();

		const studentData: Partial<IStudent> = {};
		studentFields.forEach((studentField) => {
			const dataField = dataFromLocalStorage[studentField];
			if (dataField === undefined) {
				return;
			}

			if (dataField === null) {
				studentData[studentField] = undefined;
				return;
			}

			studentData[studentField] = dataField as never;
		});

		return studentData as IStudent;
	}

	public async setData(studentData: IStudent): Promise<void> {
		const dataForLocalStorage = this.retrieveDataFromLocalStorage() as Partial<IStudent>;

		const newStudentData = merge<Partial<IStudent>, Partial<IStudent>>(dataForLocalStorage, studentData, {
			arrayMerge: (destinationArray: unknown[], sourceArray: unknown[]) =>
				this.mergeArraysWithoutDuplicates(destinationArray, sourceArray)
		});

		Object.keys(newStudentData).forEach((key) => {
			const typedKey = key as keyof Partial<IStudent>;
			if (newStudentData[typedKey] === null) {
				newStudentData[typedKey] = undefined;
			}
		});

		this.setDataInLocalStorage(newStudentData as Record<string, unknown>);
		this.broadcastChanges(newStudentData as IStudent);
	}

	private mergeArraysWithoutDuplicates(destinationArray: unknown[], sourceArray: unknown[]): unknown[] {
		return Array.from(new Set([...destinationArray, ...sourceArray]));
	}

	public async addToCollection(type: StudentField, items: any[]): Promise<void> {
		this.addToIdListInLocalStorage(type, items as number[]);
	}

	public async removeFromCollection(type: StudentField, items: any[]): Promise<void> {
		this.removeFromIdListInLocalStorage(type, items as number[]);
	}

	public async replaceCollection(type: StudentField, items: any[]): Promise<void> {
		this.replaceIdListInLocalStorage(type, items as number[]);
	}

	public async addToWriteHistory(studentFields: StudentField[], actor: Actor): Promise<void> {
		const records = this.parseStoredArray<WriteHistoryRecord>(this.storageKeyWriteHistory, []);

		for (const studentField of studentFields) {
			const existingRecord = records.find((record) => record.field === studentField);

			if (existingRecord) {
				existingRecord.actor = actor;
			} else {
				records.push({
					field: studentField,
					actor
				});
			}
		}

		this.localStorage.store(this.storageKeyWriteHistory, JSON.stringify(records), this.storageTTL);
	}

	public async getWriteHistory(): Promise<WriteHistoryRecord[]> {
		return this.parseStoredArray<WriteHistoryRecord>(this.storageKeyWriteHistory, []);
	}

	private retrieveDataFromLocalStorage(): StudentStorageData {
		return this.parseStoredObject<StudentStorageData>(this.storageKey, {});
	}

	private parseStoredObject<T extends Record<string, unknown>>(storageKey: string, fallback: T): T {
		const rawData = this.localStorage.retrieve(storageKey) as string | null;
		if (!rawData) {
			return fallback;
		}

		try {
			const parsedValue = JSON.parse(rawData) as unknown;
			if (parsedValue === null || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
				return fallback;
			}

			return parsedValue as T;
		} catch {
			return fallback;
		}
	}

	private parseStoredArray<T>(storageKey: string, fallback: T[]): T[] {
		const rawData = this.localStorage.retrieve(storageKey) as string | null;
		if (!rawData) {
			return fallback;
		}

		try {
			const parsedValue = JSON.parse(rawData) as unknown;
			if (!Array.isArray(parsedValue)) {
				return fallback;
			}

			return parsedValue as T[];
		} catch {
			return fallback;
		}
	}

	private setDataInLocalStorage(data: Record<string, unknown>): void {
		Object.keys(data).forEach((key) => {
			if (data[key] === undefined) {
				data[key] = null;
			}
		});

		this.localStorage.store(this.storageKey, JSON.stringify(data), this.storageTTL);
	}

	private addToIdListInLocalStorage(key: StudentField | InterestType, ids: number[]): void {
		const dataForLocalStorage = this.retrieveDataFromLocalStorage();
		const savedItems = new Set<number>((dataForLocalStorage[key] as number[] | undefined) ?? []);
		ids.forEach((id) => {
			savedItems.add(id);
		});

		dataForLocalStorage[key] = Array.from(savedItems);
		this.setDataInLocalStorage(dataForLocalStorage as Record<string, unknown>);
		this.broadcastChanges({ [key]: dataForLocalStorage[key] } as IStudent);
	}

	private removeFromIdListInLocalStorage(key: StudentField | InterestType, ids: number[]): void {
		const dataForLocalStorage = this.retrieveDataFromLocalStorage();
		const savedItems = new Set<number>((dataForLocalStorage[key] as number[] | undefined) ?? []);
		ids.forEach((id) => {
			savedItems.delete(id);
		});

		dataForLocalStorage[key] = Array.from(savedItems);
		this.setDataInLocalStorage(dataForLocalStorage as Record<string, unknown>);
		this.broadcastChanges({ [key]: dataForLocalStorage[key] } as IStudent);
	}

	private replaceIdListInLocalStorage(key: StudentField | InterestType, ids: number[]): void {
		const dataForLocalStorage = this.retrieveDataFromLocalStorage();

		dataForLocalStorage[key] = [...ids];
		this.setDataInLocalStorage(dataForLocalStorage as Record<string, unknown>);
		this.broadcastChanges({ [key]: dataForLocalStorage[key] } as IStudent);
	}

	private broadcastChanges(changes: IStudent): void {
		if (!this.anonymousStudentEventBroadcaster) {
			return;
		}

		const event = new AnonymousStudentProfileUpdated(new Date(), StudentRepositoryStateType.OFFLINE, changes, true);
		this.anonymousStudentEventBroadcaster.broadcastProfileUpdatedEvent(event);
	}
}
